import {useState, useRef, useEffect, useMemo} from 'react';
import AccessibleTypeahead from 'components/Forms/AccessibleTypeahead';
import {TypeaheadRef} from 'react-bootstrap-typeahead';
import {FaMapMarkerAlt, FaMicrophone, FaStop, FaTrash} from 'react-icons/fa';
import debounce from 'lodash/debounce';
import {
  GeolocatorProps,
  LocationData,
  GEOLOCATION_OPTIONS,
  normalizeManualLocality,
} from './geolocationUtils';
import {uuidv4} from 'lib0/random';
import {
  PlaceSuggestion,
  usePlaceFromGeocode,
  usePlaceSuggestion,
  usePlaceDetails,
} from '../firebase/functions/geolocationApi';
import {isNullOrUndefined} from 'utils/general';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useUserId} from 'hooks/user/useUserId';
import {doc, getFirestore, updateDoc} from 'firebase/firestore';
import {useAudioRecorder} from 'react-audio-voice-recorder';
import {uploadAudioFile} from './uploadAudio';
import {useParams} from 'react-router-dom';
import styled from 'styled-components';
import {useCourseTheme} from 'course/contexts/ThemeContext';
import COUNTRIES, {type Country} from 'data/countries/country_names';

/** Debounce before a settled search input fires a Places Autocomplete request. */
const PLACE_SUGGESTION_DEBOUNCE_MS = 300;

/**
 * How long a Places Autocomplete request may go unanswered before the lookup
 * counts as unreachable. A filtered network drops the request instead of
 * refusing it, so without this the applicant waits out the callable deadline.
 */
const PLACE_SUGGESTION_TIMEOUT_MS = 10000;

const LOOKUP_TIMED_OUT = 'timeout';

/** Why the place lookup was judged unreachable. */
type LookupFailureCause = typeof LOOKUP_TIMED_OUT | 'no-response';

const LOOKUP_UNAVAILABLE_MESSAGE =
  'We can\'t reach the location search right now. Type your city and choose "Use what I typed".';

/**
 * Countries offered alongside a manually typed locality. `CIP` is the
 * "Somewhere on Earth" placeholder used elsewhere, not a country.
 */
const SELECTABLE_COUNTRIES = COUNTRIES.filter(country => country.code !== 'CIP');

/** The option the typeahead appends for the typed text while `allowNew` is on. */
interface ManualLocalityOption {
  customOption: true;
  locality: string;
}

const isManualLocalityOption = (
  option: PlaceSuggestion | ManualLocalityOption,
): option is ManualLocalityOption => 'customOption' in option;

/**
 * Flattens what is known about a failed lookup into one log payload. The
 * production console forwarder serializes only one level deep, so a nested
 * `Error` would arrive empty.
 */
const describeLookupFailure = (
  cause: LookupFailureCause | null,
  lookupError: Error | null,
) => ({
  cause,
  errorCode:
    lookupError && 'code' in lookupError ? String(lookupError.code) : null,
  errorMessage: lookupError?.message ?? null,
  isOnline: navigator.onLine,
});

export const Geolocator = ({
  field,
  name,
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
}: GeolocatorProps) => {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  // What the box holds, and what `suggestions` were fetched for. While they
  // differ there is a search outstanding, which covers the debounce gap as well
  // as the request — comparing the two rather than flipping a flag means a
  // slow reply for an earlier keystroke cannot clear the state early.
  const [inputText, setInputText] = useState('');
  const [resolvedQuery, setResolvedQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLookupFailed, setHasLookupFailed] = useState(false);
  const [sessionToken, setSessionToken] = useState(() => uuidv4());
  const t = useTranslateFunction('application');

  const typeaheadRef = useRef<TypeaheadRef>(null);
  const currentLocation = values[name];
  const currentValue = currentLocation?.locality || '';
  const isManualEntry = Boolean(currentLocation?.isManualEntry);
  const selectedCountry = SELECTABLE_COUNTRIES.filter(
    country => country.code === currentLocation?.country,
  );
  const rawError = errors[name];
  const isTouched = touched[name] || false;

  let errorMessage = null;
  let countryErrorMessage = null;
  if (rawError) {
    if (typeof rawError === 'string') {
      errorMessage = rawError;
    } else if (typeof rawError === 'object' && 'eng_name' in rawError) {
      errorMessage = rawError.eng_name;
    } else if (
      typeof rawError === 'object' &&
      'country' in rawError &&
      typeof rawError.country === 'string'
    ) {
      countryErrorMessage = rawError.country;
    }
  }

  const [getPlaceSuggestion, , placeSuggestionError] = usePlaceSuggestion();
  const [getPlaceFromGeocode] = usePlaceFromGeocode();
  const [getPlaceDetails] = usePlaceDetails();

  // The callable hook clears its error at the start of every request, so keep
  // the last real one for the logs below.
  const lookupFailureCauseRef = useRef<LookupFailureCause | null>(null);
  const lastLookupErrorRef = useRef<Error | null>(null);
  useEffect(() => {
    if (placeSuggestionError) {
      lastLookupErrorRef.current = placeSuggestionError;
    }
  }, [placeSuggestionError]);

  // A client that cannot reach the lookup usually cannot reach the log callable
  // either; `isManualEntry` on the saved answer is the record that always lands.
  useEffect(() => {
    if (!hasLookupFailed) {
      return;
    }
    console.warn(
      '[Geolocator] Place lookup unreachable; offering manual entry',
      describeLookupFailure(
        lookupFailureCauseRef.current,
        lastLookupErrorRef.current,
      ),
    );
  }, [hasLookupFailed]);

  // Read by the debounced search through refs so its timer is never recreated.
  const sessionTokenRef = useRef(sessionToken);
  const getPlaceSuggestionRef = useRef(getPlaceSuggestion);
  useEffect(() => {
    sessionTokenRef.current = sessionToken;
  }, [sessionToken]);
  useEffect(() => {
    getPlaceSuggestionRef.current = getPlaceSuggestion;
  }, [getPlaceSuggestion]);

  const updateLocationField = (locationData: LocationData) => {
    setFieldValue(name, locationData);
    setError(null);
    setSessionToken(uuidv4());
  };

  const updateTypeaheadDisplay = (locality: string) => {
    typeaheadRef.current?.clear();
    typeaheadRef.current?.setState({text: locality});
  };

  const handleChange = async (
    selected: (PlaceSuggestion | ManualLocalityOption)[],
  ) => {
    if (selected.length === 0) {
      setFieldValue(name, null);
      return;
    }

    const selection = selected[0];
    if (isManualLocalityOption(selection)) {
      const locality = normalizeManualLocality(selection.locality);
      if (isNullOrUndefined(locality)) {
        setFieldValue(name, null);
        setError('Please enter the name of your city');
        return;
      }
      // The typed text stays out of the log: it is free text, and the saved
      // answer already holds it.
      console.warn(
        '[Geolocator] Applicant kept a manually typed location',
        describeLookupFailure(
          lookupFailureCauseRef.current,
          lastLookupErrorRef.current,
        ),
      );
      updateLocationField({locality, isManualEntry: true});
      return;
    }

    let formattedCoordinates = '';
    let country: string | undefined;

    // Reuse the active session token so this Place Details request terminates
    // the autocomplete session, billing it at the discounted per-session SKU.
    try {
      const response = await getPlaceDetails({
        placeId: selection.placeId,
        sessionToken,
      });

      if (isNullOrUndefined(response) || isNullOrUndefined(response.data)) {
        console.error(response);
      } else {
        const details = response.data;
        formattedCoordinates = `${details.latitude},${details.longitude}`;
        country = details.countryCode;
      }
    } catch (err) {
      console.debug('[Geolocator] Place Details request failed', err);
      setError('Failed to fetch location coordinates');
    }

    const locationData: LocationData = {
      locality: selection.locality,
      coordinates: formattedCoordinates,
      ...(isNullOrUndefined(country) ? {} : {country}),
    };
    updateLocationField(locationData);
  };

  // A manual entry has no Place Details to take the country from, so the
  // applicant picks it; the profile mirror and the flags read that code.
  const handleCountryChange = (selected: Country[]) => {
    if (isNullOrUndefined(currentLocation)) {
      return;
    }
    setFieldTouched(name, true);
    const withoutCountry: LocationData = {
      locality: currentLocation.locality,
      isManualEntry: true,
    };
    setFieldValue(
      name,
      selected.length === 0
        ? withoutCountry
        : {...withoutCountry, country: selected[0].code},
    );
  };

  const handleSearch = (query: string) => {
    setFieldTouched(name, true);
    setInputText(query);
    debouncedSearch(query);
  };

  // Created once via useMemo (empty deps) so the same timer is reused; the
  // latest session token and callable are read through refs.
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchInput: string) => {
        if (!searchInput.trim()) {
          setSuggestions([]);
          setResolvedQuery(searchInput);
          return;
        }

        const markLookupFailed = (cause: LookupFailureCause) => {
          lookupFailureCauseRef.current = cause;
          setHasLookupFailed(true);
          setError(LOOKUP_UNAVAILABLE_MESSAGE);
          setSuggestions([]);
          setResolvedQuery(searchInput);
        };

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timedOut = new Promise<typeof LOOKUP_TIMED_OUT>(resolve => {
          timeoutId = setTimeout(
            () => resolve(LOOKUP_TIMED_OUT),
            PLACE_SUGGESTION_TIMEOUT_MS,
          );
        });

        try {
          const response = await Promise.race([
            getPlaceSuggestionRef.current({
              searchInput,
              sessionToken: sessionTokenRef.current,
            }),
            timedOut,
          ]);
          if (response === LOOKUP_TIMED_OUT) {
            markLookupFailed(LOOKUP_TIMED_OUT);
            return;
          }
          // The callable hook resolves undefined on failure instead of rejecting.
          if (isNullOrUndefined(response) || isNullOrUndefined(response.data)) {
            markLookupFailed('no-response');
            return;
          }
          const formattedSuggestions: PlaceSuggestion[] = response.data;
          setSuggestions(formattedSuggestions);
          setResolvedQuery(searchInput);
          setHasLookupFailed(false);
          setError(null);
        } catch (err) {
          console.debug('[Geolocator] Autocomplete request failed', err);
          markLookupFailed('no-response');
        } finally {
          clearTimeout(timeoutId);
        }
      }, PLACE_SUGGESTION_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const isSearchPending =
    inputText.trim().length > 0 && inputText !== resolvedQuery;

  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoading(true);

    const onSuccess = async (position: GeolocationPosition) => {
      const {latitude: lat, longitude: lng} = position.coords;

      try {
        const response = await getPlaceFromGeocode({
          latitude: lat,
          longitude: lng,
        });

        if (!response || !response.data) {
          lookupFailureCauseRef.current = 'no-response';
          setHasLookupFailed(true);
          setError(LOOKUP_UNAVAILABLE_MESSAGE);
          return;
        }

        const place = await response.data;
        const locationData: LocationData = {
          locality: place.locality,
          coordinates: `${lat},${lng}`,
          ...(isNullOrUndefined(place.countryCode)
            ? {}
            : {country: place.countryCode}),
        };

        updateLocationField(locationData);
        updateTypeaheadDisplay(locationData.locality);
      } catch (err) {
        console.debug('[Geolocator] Reverse-geocode request failed', err);
        setError('Error tracking location');
      } finally {
        setIsLoading(false);
      }
    };

    const onError = (err: GeolocationPositionError) => {
      console.debug('[Geolocator] Browser geolocation failed', err.message);
      setError('We could not get your current location');
      setIsLoading(false);
    };

    navigator.geolocation.getCurrentPosition(
      onSuccess,
      onError,
      GEOLOCATION_OPTIONS,
    );
  };

  return (
    <div className="question-spacing">
      <label className="form-label">{field.question}</label>

      {/* Above the input: the open suggestion menu covers whatever is below. */}
      {error ? (
        <div className="error" role="alert">
          {t(error)}
        </div>
      ) : null}

      <div className="location-input-container">
        <div className="input-group">
          <AccessibleTypeahead
            ref={typeaheadRef}
            id={`${name}-location-input`}
            className="flex-grow-1"
            labelKey="locality"
            onChange={handleChange}
            onInputChange={handleSearch}
            options={suggestions}
            // Places already searched for this input, so keep every suggestion:
            // the default filter drops any label the typed text isn't a literal
            // substring of, which hides the right city for inputs like
            // "Tehran, Iran" (labelled "Tehran, Tehran Province, Iran").
            filterBy={() => true}
            placeholder={t('Enter city...') as string}
            defaultInputValue={currentValue}
            isLoading={isLoading || isSearchPending}
            minLength={2}
            // The typeahead ignores highlightOnlyResult while allowNew is on.
            highlightOnlyResult={!hasLookupFailed}
            allowNew={hasLookupFailed}
            // The space is added here so a translation cannot lose it.
            newSelectionPrefix={`${t('Use what I typed:') as string} `}
            emptyLabel={
              isSearchPending
                ? (t('Searching...') as string)
                : (t('No locations found') as string)
            }
          />
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleGeolocation}
            title={t('Use my current location') as string}
            style={{borderLeft: 'none'}}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <FaMapMarkerAlt />
            )}
          </button>
        </div>
      </div>

      {isManualEntry ? (
        <div className="mt-2">
          <label className="form-label" htmlFor={`${name}-country-input`}>
            {t('Country')}
          </label>
          <AccessibleTypeahead
            id={`${name}-country-typeahead`}
            labelKey="eng_name"
            options={SELECTABLE_COUNTRIES}
            onChange={handleCountryChange}
            defaultSelected={selectedCountry}
            placeholder={t('Choose your country') as string}
            inputProps={{id: `${name}-country-input`}}
          />
          {isTouched && countryErrorMessage ? (
            <div className="error">{t(countryErrorMessage)}</div>
          ) : null}
        </div>
      ) : null}

      {!error && isTouched && errorMessage ? (
        <div className="error">{t(errorMessage)}</div>
      ) : null}
    </div>
  );
};

export const NameRecordingInput = ({
  name,
  values,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  touched,
  setFieldValue,
  setFieldTouched,
}) => {
  const value: Blob | string | null = values[name];
  const {targetCourseId} = useParams();
  const userId = useUserId();
  const db = getFirestore();
  const [optOut, setOptOut] = useState<boolean>(value === 'optout');
  const [deleted, setDeleted] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const {
    startRecording,
    stopRecording,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    togglePauseResume,
    recordingBlob,
    isRecording,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    isPaused,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    recordingTime,
  } = useAudioRecorder();
  const startTimeRef = useRef(0);
  const t = useTranslateFunction('application');
  const {buttonColorPrimary} = useCourseTheme();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const setErrorState = (error: Error | null) => {
    setUploadProgress(0);
  };

  const onUploadComplete = (url: string) => {
    setFieldValue(name, url);
    setUploadProgress(0);
    const endTime = Date.now();
    const startTime = startTimeRef.current;
    const duration = endTime - startTime;
    const blobSize = recordingBlob.size;
    const bitRate = blobSize / (duration / 1000);
    const applicationPath = `users/${userId}/${targetCourseId}/studentApplication`;
    updateDoc(doc(db, applicationPath), {bitRate});
  };

  const onClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      setUploadProgress(1);
      startRecording();
    }
  };

  useEffect(() => {
    setFieldTouched(name, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [recordingBlob, optOut]);

  useEffect(() => {
    if (recordingBlob) {
      console.log(recordingBlob);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setDeleted(false);
      setFieldValue(name, null);
      startTimeRef.current = Date.now();
      uploadAudioFile(
        recordingBlob,
        onUploadComplete,
        progress => console.log(progress),
        setErrorState,
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [recordingBlob]);

  useEffect(() => {
    if (optOut) {
      setFieldValue(name, 'optout');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [optOut]);

  useEffect(() => {
    if (deleted) {
      setFieldValue(name, null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [deleted]);

  let audioBlob: Blob | string | null = value !== 'optout' ? value : null;
  audioBlob = deleted ? null : audioBlob;

  const isUploading =
    !isRecording && uploadProgress > 0 && uploadProgress < 100;
  const recordText = audioBlob && !isUploading ? 'Re-record' : 'Record';
  const buttonText =
    isRecording || isUploading ? (
      <FaStop />
    ) : (
      <>
        <FaMicrophone aria-hidden="true" /> <span>{t(recordText)} {t('Name')}</span>
      </>
    );
  const canOptOut = !isRecording && !audioBlob && !isUploading;
  const optOutText = optOut ? 'Opt back in' : 'Opt out';
  const showTrash = audioBlob && !isUploading && !isRecording;

  return (
    <div>
      <div className="d-flex align-items-center" style={{height: 60}}>
        {!optOut && (
          <RecordButton
            color={buttonColorPrimary}
            disabled={isUploading}
            className="btn btn-primary mr-2"
            type="button"
            onClick={onClick}
          >
            {buttonText}
          </RecordButton>
        )}
        {canOptOut && (
          <button
            className="btn btn-secondary mr-2"
            type="button"
            onClick={() => setOptOut(!optOut)}
          >
            {t(optOutText)}
          </button>
        )}
        {isRecording && <span>{t('Recording...')}</span>}
        {!optOut && !isUploading && !isRecording && (
          <span className="mr-2">
            <AudioBlob audioBlob={audioBlob} />
          </span>
        )}
        {isUploading && <span>{t('Uploading...')}</span>}
        {showTrash && (
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            onClick={() => setDeleted(true)}
          >
            <FaTrash />
          </button>
        )}
      </div>
    </div>
  );
};

export const AudioBlob = ({audioBlob}: {audioBlob: Blob | string | null}) => {
  if (!audioBlob) {
    return null;
  }
  // A previously uploaded recording is stored as a URL string.
  if (typeof audioBlob === 'string') {
    // eslint-disable-next-line jsx-a11y/media-has-caption -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    return <audio src={audioBlob} controls />;
  }
  const url = URL.createObjectURL(audioBlob);
  // eslint-disable-next-line jsx-a11y/media-has-caption -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  return <audio src={url} controls />;
};

const RecordButton = styled.button`
  background-color: ${props => props.color};
  border-color: ${props => props.color};
  color: 'white';

  &:hover {
    filter: brightness(0.85);
  }
`;
